import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import * as Sentry from '@sentry/angular';
import { environment } from '../../../environments/environment';
import { FeedbackActor } from './sentry-feedback.types';
import { UserStore } from './user.store';

/**
 * How long a surface may stay without a reachable trigger before the floating button steps in.
 * Long enough for a cold load to finish rendering its toolbar, short enough to feel immediate.
 */
const MOUNT_DELAY_MS = 400;

/**
 * Handles everything that wires Sentry's feedback widget into this app.
 *
 * 1. **User context** — mirrors `UserStore` state into `Sentry.setUser()` so
 *    feedback reports and replays carry the current (or impersonated) user.
 *
 * 2. **Trigger registry** — the widget runs with `autoInject: false` (see main.ts), so the app
 *    owns its own trigger: `FeedbackTriggerButtonComponent` calls {@link registerTrigger} to hand
 *    Sentry a button to open the form from. That button lives in the toolbar, which is where a
 *    feedback affordance is discoverable and where it collides with nothing.
 *
 * 3. **Contextual actor** — one rule fills every gap the toolbar leaves: *Sentry's own floating
 *    button appears exactly when the toolbar is not reachable.* That is two cases, and only two:
 *      - no trigger is mounted at all — the chrome-less `empty` shell (login, error and public
 *        pages), which has no toolbar to put one in;
 *      - a dialog is open — the toolbar is still painted, but the CDK backdrop swallows the click.
 *    Deriving the first case from "is a trigger mounted" rather than from a list of layout names
 *    keeps it honest: a new shell that forgets the toolbar button gets the floating fallback for
 *    free instead of silently losing feedback.
 *
 * 4. **Top-layer promotion** — Angular CDK 21+ renders dialogs via the native Popover API
 *    (`popover="manual"`). Popovers live in the browser's top layer, which sits above every
 *    z-index — so the widget (fixed-position, even at `z-index: 100000`) would be buried by any
 *    open dialog. Sentry renders BOTH the actor and the form into the same `#sentry-feedback`
 *    host, so promoting that host to a manual popover covers both; because top-layer stacking is
 *    LIFO, it is re-shown every time another popover is added. Without this the feedback form
 *    opened from the contextual actor would render *behind* the dialog it is reporting on.
 */
@Injectable({
  providedIn: 'root',
})
export class SentryFeedbackService {
  readonly #userStore = inject(UserStore);
  readonly #document = inject(DOCUMENT);
  readonly #destroyRef = inject(DestroyRef);
  readonly #dialog = inject(MatDialog);

  /** Whether the feedback widget runs at all; the trigger button renders only when it does. */
  readonly enabled = environment.sentry.enabled && environment.sentry.feedbackEnabled;

  #feedbackEl: HTMLElement | null = null;
  #bootObserver: MutationObserver | null = null;
  #popoverObserver: MutationObserver | null = null;

  /** Sentry's floating button, created on first use and then re-used across mounts. */
  #actor: FeedbackActor | null = null;
  #actorMounted = false;

  /** Pending mount of the floating button — see {@link #scheduleSync}. */
  #mountTimer: ReturnType<typeof setTimeout> | null = null;

  /** How many app-owned trigger buttons are currently in the DOM. */
  readonly #triggerCount = signal(0);

  /** How many dialogs are currently open; anything above 0 means the toolbar is unclickable. */
  readonly #openDialogs = signal(0);

  /** A dialog owns the screen, so the toolbar is painted but not clickable. */
  readonly dialogOpen = computed(() => this.#openDialogs() > 0);

  /** See point 3 in the class docs: no reachable trigger, or one that a dialog has covered. */
  readonly actorVisible = computed(() => this.#triggerCount() === 0 || this.dialogOpen());

  constructor() {
    if (!environment.sentry.enabled) {
      return;
    }

    effect(() => {
      const loggedIn = this.#userStore.loggedin();
      const activeUser = this.#userStore.impersonatedUser() ?? this.#userStore.currentUser();
      const originalUser = this.#userStore.originalUser();

      if (!loggedIn || !activeUser) {
        Sentry.setUser(null);
        Sentry.setContext('impersonation', null);
        return;
      }

      Sentry.setUser({
        id: activeUser.userId ?? undefined,
        email: activeUser.emailAddress ?? undefined,
        username: activeUser.name ?? undefined,
      });

      if (originalUser) {
        Sentry.setContext('impersonation', {
          originalUserId: originalUser.userId ?? null,
          originalUserEmail: originalUser.emailAddress ?? null,
        });
      } else {
        Sentry.setContext('impersonation', null);
      }
    });

    if (this.enabled) {
      this.#initTopLayer();
      this.#trackOpenDialogs();
      effect(() => this.#scheduleSync(this.actorVisible()));
      this.#destroyRef.onDestroy(() => {
        this.#bootObserver?.disconnect();
        this.#popoverObserver?.disconnect();
        if (this.#mountTimer !== null) {
          clearTimeout(this.#mountTimer);
        }
      });
    }
  }

  /**
   * Hand Sentry a button of ours to open the feedback form from, and count it as a reachable
   * trigger so the floating fallback stands down while it is on screen.
   *
   * @returns teardown to call when the button leaves the DOM.
   */
  registerTrigger(el: HTMLElement): () => void {
    // Counted even when Sentry itself is unavailable (widget disabled, init failed, tests): the
    // count answers "is a trigger on screen", and without Sentry there is no actor to fall back
    // to either — so a truthful count beats a defensive one.
    const detach = Sentry.getFeedback()?.attachTo(el);
    this.#triggerCount.update(count => count + 1);

    let released = false;
    return () => {
      if (released) {
        return;
      }
      released = true;
      detach?.();
      this.#triggerCount.update(count => Math.max(0, count - 1));
    };
  }

  /**
   * Keep {@link #openDialogs} in step with `MatDialog.openDialogs`.
   *
   * `afterAllClosed` alone would miss "two open, one closes", and `afterClosed()` fires around the
   * same tick as Material's own bookkeeping — so the count is re-read on a microtask after each
   * close rather than assumed. `afterAllClosed` then acts as the authoritative floor.
   */
  #trackOpenDialogs(): void {
    const sync = () => this.#openDialogs.set(this.#dialog.openDialogs.length);

    this.#dialog.afterOpened.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(ref => {
      sync();
      ref
        .afterClosed()
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => queueMicrotask(sync));
    });

    this.#dialog
      .afterAllClosed.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#openDialogs.set(0));
  }

  /**
   * Mount late, unmount immediately.
   *
   * The service is constructed at bootstrap, so on a cold load its effect runs while the trigger
   * count is still 0 — the toolbar only registers its button once MSAL, the resolvers and the
   * layout have all landed, which is a real gap, not a frame. Mounting straight away would flash
   * the floating button on every page load of an authenticated surface. Waiting
   * {@link MOUNT_DELAY_MS} lets the shell settle first; a fallback that is a fraction of a second
   * late costs nothing, and the same delay quietly swallows the actor for dialogs that open and
   * close again immediately.
   *
   * Going away is never delayed: the moment a trigger is reachable the fallback is redundant.
   *
   * Deliberately a timer and not `requestAnimationFrame`: rAF does not fire while the tab is
   * hidden, which would leave the widget's state frozen behind whatever the user last saw.
   */
  #scheduleSync(needed: boolean): void {
    if (this.#mountTimer !== null) {
      clearTimeout(this.#mountTimer);
      this.#mountTimer = null;
    }

    if (!needed || this.#actorMounted) {
      this.#syncActor(needed);
      return;
    }

    this.#mountTimer = setTimeout(() => {
      this.#mountTimer = null;
      this.#syncActor(true);
    }, MOUNT_DELAY_MS);
  }

  /**
   * Mount or unmount Sentry's floating button.
   *
   * Position is left to Sentry's own default (bottom-right, 16px margin) — the widget is never
   * moved. An earlier version parked it bottom-left while a dialog was open, to stay clear of the
   * right-aligned `<mat-dialog-actions>`; that was dropped on request. The trade-off it carried is
   * real: over a near-fullscreen dialog the button sits on top of that dialog's own buttons.
   */
  #syncActor(needed: boolean): void {
    if (needed) {
      if (!this.#actor) {
        // createWidget() both creates the actor and appends it; it also creates the
        // `#sentry-feedback` host on first call, which is what #initTopLayer is waiting for.
        this.#actor = Sentry.getFeedback()?.createWidget() ?? null;
        this.#wireIfPresent();
      } else if (!this.#actorMounted) {
        this.#actor.appendToDom();
      }
      this.#actorMounted = !!this.#actor;
    } else if (this.#actorMounted) {
      this.#actor?.removeFromDom();
      this.#actorMounted = false;
    }
  }

  #initTopLayer(): void {
    // With autoInject off, `#sentry-feedback` only appears once something renders into it — the
    // contextual actor, or the form opened from a toolbar trigger. Wire whichever comes first.
    if (this.#wireIfPresent()) {
      return;
    }

    this.#bootObserver = new MutationObserver(() => this.#wireIfPresent());
    this.#bootObserver.observe(this.#document.body, { childList: true, subtree: true });
  }

  /** @returns whether the host exists and is wired (idempotent — safe to call on every mutation). */
  #wireIfPresent(): boolean {
    if (this.#feedbackEl) {
      return true;
    }
    const el = this.#document.getElementById('sentry-feedback');
    if (!el) {
      return false;
    }

    this.#bootObserver?.disconnect();
    this.#bootObserver = null;
    this.#wire(el);
    return true;
  }

  #wire(el: HTMLElement): void {
    this.#feedbackEl = el;

    try {
      el.setAttribute('popover', 'manual');
      el.showPopover();
    } catch (err) {
      // Browsers without Popover API support: fall back silently to the
      // pre-fix behaviour (widget hidden by dialogs — no regression).
      console.warn('[SentryFeedback] showPopover failed', err);
      return;
    }

    // Scope must be `document.body` (not `.cdk-overlay-container`) because
    // CDK 21 supports two popover placements and Material uses both:
    //   - `global` (inside `.cdk-overlay-container`): MatDialog.
    //   - `inline` (next to the trigger element): mat-select, mat-menu,
    //     mat-autocomplete, mat-tooltip, mat-timepicker —
    //     see `node_modules/@angular/material/fesm2022/select.mjs:288`
    //     which sets `_popoverLocation = 'inline'`.
    // A container-scoped observer misses the inline case.
    this.#popoverObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        const { addedNodes } = mutation;
        for (let i = 0; i < addedNodes.length; i++) {
          const node = addedNodes[i];
          if (
            node instanceof HTMLElement &&
            node !== this.#feedbackEl &&
            (node.hasAttribute('popover') || node.querySelector?.('[popover]'))
          ) {
            this.#repromote();
            return;
          }
        }
      }
    });
    this.#popoverObserver.observe(this.#document.body, { childList: true, subtree: true });
  }

  #repromote(): void {
    const el = this.#feedbackEl;
    if (!el) return;
    // rAF ensures the CDK popover has actually called showPopover() before
    // we stack on top of it.
    requestAnimationFrame(() => {
      try {
        el.hidePopover();
        el.showPopover();
      } catch (err) {
        console.warn('[SentryFeedback] repromote failed', err);
      }
    });
  }
}
